import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Clock, Users, Globe } from 'lucide-react';

export interface PricingTierBonusTemplate {
  id: number;
  type?: string;
  initialValue?: unknown;
  translations?: Record<string, { name?: string; description?: string }>;
}

export interface AccessBonusValue {
  accessTargetType: 'COURSE' | 'CHANNEL' | 'BOOK';
  selectionMode?: 'specific' | 'quantity';
  targetId: number;
  targetName: string;
  quantity?: number;
  courseType?: string;
}

const ACCESS_TARGET_LABELS: Record<string, string> = {
  COURSE: 'Курс',
  CHANNEL: 'Канал',
  BOOK: 'Книга',
};

export function getAccessTargetLabel(type: string): string {
  return ACCESS_TARGET_LABELS[type] ?? type;
}

export function parseAccessBonusValue(bonus: PricingTierBonusTemplate): AccessBonusValue | null {
  const val = bonus.initialValue;
  if (val && typeof val === 'object' && !Array.isArray(val) && 'accessTargetType' in val) {
    return val as AccessBonusValue;
  }
  if (typeof val === 'string') {
    try {
      const parsed: unknown = JSON.parse(val);
      if (
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        'accessTargetType' in parsed
      ) {
        return parsed as AccessBonusValue;
      }
    } catch {
      return null;
    }
  }
  return null;
}

export function isAccessBonus(bonus: PricingTierBonusTemplate): boolean {
  return parseAccessBonusValue(bonus) !== null;
}

export interface PricingTier {
  id: number;
  channelPassId?: number | null;
  courseId?: number | null;
  subscriptionPlanId?: number | null;
  bookId?: number | null;
  name?: string;
  prices: Record<string, number>;
  baseCurrency: string;
  promoPrices?: Record<string, number> | null;
  durationDays?: number | null;
  isActive: boolean;
  userLimit?: number | null;
  targetLanguages?: string[];
  translations?: Record<string, { name?: string }>;
  order?: number;
  isFamily?: boolean;
  familySlots?: number;
  bonuses?: Array<{ id: number; name?: string; type?: string }>;
  bonusTemplates?: PricingTierBonusTemplate[];
}

interface PricingTiersTooltipProps {
  tiers: PricingTier[];
  className?: string;
  targetType?: 'course' | 'subscription' | 'channel-pass' | 'book';
}

const ZERO_DECIMAL_CURRENCIES = new Set(['UZS']);

export function formatLanguageCode(code: string): string {
  const languageNames: Record<string, string> = {
    ru: 'Русский',
    uz: 'Узбекский',
    en: 'Английский',
  };
  return languageNames[code] || code.toUpperCase();
}

export function formatLanguageCodes(codes: string[]): string {
  if (!codes || codes.length === 0) return '';
  if (codes.length === 1) return formatLanguageCode(codes[0]);
  return codes.map(formatLanguageCode).join(' и ');
}

export function formatCurrencyPrice(amount: number, currency: string): string {
  const fractionDigits = ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2;

  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);

  return `${formatted} ${currency}`;
}

export function formatDuration(durationDays: number | null): string {
  if (durationDays === null) {
    return 'Бессрочно';
  }
  if (durationDays === 30) {
    return '1 месяц';
  }
  if (durationDays === 90) {
    return '3 месяца';
  }
  if (durationDays === 180) {
    return '6 месяцев';
  }
  if (durationDays === 365) {
    return '1 год';
  }
  return `${durationDays} дн.`;
}

function getTargetTypeLabel(type?: string): string {
  switch (type) {
    case 'course':
      return 'Курс';
    case 'subscription':
      return 'Абонемент';
    case 'channel-pass':
      return 'Подписка на канал';
    case 'book':
      return 'Книга';
    default:
      return 'Тариф';
  }
}

function TierCard({ tier, targetType: _targetType, index }: { tier: PricingTier; targetType?: string; index: number }) {
  const activeCurrencies = Object.entries(tier.prices).filter(([_, price]) => price > 0);
  
  // Получаем название тарифа из translations или используем индекс
  const tierName = tier.name || tier.translations?.ru?.name || tier.translations?.['ru']?.name || `Тариф ${index + 1}`;
  
  return (
    <div className="p-3 space-y-2 bg-linear-to-br from-background to-muted/30 border border-border/50 rounded-lg">
      {/* Название тарифа */}
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm">{tierName}</span>
        {tier.isActive ? (
          <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20">
            Активен
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] bg-gray-500/10 text-gray-500 border-gray-500/20">
            Неактивен
          </Badge>
        )}
      </div>

      {/* Цены по валютам */}
      <div className="space-y-1">
        {activeCurrencies.map(([currency, price]) => (
          <div key={currency} className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{currency}</span>
            <span className="font-mono font-medium">{formatCurrencyPrice(price, currency)}</span>
          </div>
        ))}
        {activeCurrencies.length === 0 && (
          <div className="text-sm text-muted-foreground">Бесплатно</div>
        )}
      </div>

      {/* Дополнительная информация */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground pt-1 border-t border-border/50">
        {/* Срок действия */}
        {tier.durationDays !== null && tier.durationDays !== undefined && (
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatDuration(tier.durationDays)}</span>
          </div>
        )}
        
        {/* Лимит пользователей */}
        {tier.userLimit !== undefined && tier.userLimit !== null && (
          <div className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            <span>{tier.userLimit} чел.</span>
          </div>
        )}
        
        {/* Языки */}
        {tier.targetLanguages && tier.targetLanguages.length > 0 && (
          <div className="flex items-center gap-1">
            <Globe className="h-3 w-3" />
            <span>{tier.targetLanguages.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function PricingTiersTooltip({ tiers, className, targetType }: PricingTiersTooltipProps) {
  if (!tiers || tiers.length === 0) {
    return <span className={cn('text-muted-foreground', className)}>Цена не указана</span>;
  }

  const activeTiers = tiers.filter((t) => t.isActive);
  if (activeTiers.length === 0) {
    return <span className={cn('text-muted-foreground', className)}>Цена не указана</span>;
  }

  // Все базовые цены = 0 → Бесплатно
  const allZero = activeTiers.every((t) => {
    const basePrice = t.prices[t.baseCurrency];
    return basePrice === 0;
  });
  if (allZero) {
    return <span className={cn('text-muted-foreground', className)}>Бесплатно</span>;
  }

  // Один тариф — показываем цену с тултипом
  if (activeTiers.length === 1) {
    const tier = activeTiers[0];
    const basePrice = tier.prices[tier.baseCurrency];
    const baseDisplay = basePrice !== undefined
      ? formatCurrencyPrice(basePrice, tier.baseCurrency)
      : '—';

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('cursor-help border-b border-dotted border-current', className)}>
            {baseDisplay}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" align="start" className="p-0">
          <div className="p-2">
            <TierCard tier={tier} targetType={targetType} index={0} />
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Несколько тарифов — показываем диапазон с тултипом всех тарифов
  const basePrices = activeTiers
    .map((t) => t.prices[t.baseCurrency])
    .filter((p): p is number => p !== undefined && p !== null);

  if (basePrices.length === 0) {
    return <span className={cn('text-muted-foreground', className)}>Цена не указана</span>;
  }

  const baseCurrency = activeTiers[0].baseCurrency;
  const min = Math.min(...basePrices);
  const max = Math.max(...basePrices);
  const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

  const priceDisplay = min === max
    ? `${fmt(min)} ${baseCurrency}`
    : `${fmt(min)} – ${fmt(max)} ${baseCurrency}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('cursor-help border-b border-dotted border-current', className)}>
          {priceDisplay}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" align="start" className="p-0 max-w-md">
        <div className="p-3 space-y-2">
          <div className="text-sm font-semibold mb-2 pb-2 border-b">
            {getTargetTypeLabel(targetType)} — {activeTiers.length} тариф{activeTiers.length > 1 ? (activeTiers.length < 5 ? 'а' : 'ов') : ''}
          </div>
          <div className="space-y-2 max-h-100 overflow-y-auto">
            {activeTiers.map((tier, index) => (
              <TierCard key={tier.id} tier={tier} targetType={targetType} index={index} />
            ))}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
